import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 100px;
  z-index: 3000;
`;

const Palette = styled.div`
  background: white;
  border-radius: 8px;
  width: 600px;
  max-width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border: none;
  border-bottom: 1px solid #e1e4e8;
  font-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
  }
`;

const CommandList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const CommandItem = styled.div`
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${props => props.$selected ? '#f6f8fa' : 'white'};
  border-left: 3px solid ${props => props.$selected ? '#0969da' : 'transparent'};
  
  &:hover {
    background: #f6f8fa;
  }
`;

const CommandIcon = styled.div`
  font-size: 20px;
`;

const CommandContent = styled.div`
  flex: 1;
`;

const CommandTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #24292f;
`;

const CommandDesc = styled.div`
  font-size: 12px;
  color: #586069;
`;

const CommandShortcut = styled.div`
  font-size: 11px;
  color: #586069;
  background: #f6f8fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: monospace;
`;

function CommandPalette({ onClose, onCommand }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);

  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const mod = isMac ? '⌘' : 'Ctrl';
  
  const commands = [
    { id: 'new', icon: '🆕', title: 'New Spec', desc: 'Create a new OpenAPI specification', shortcut: `${mod}+N` },
    { id: 'import', icon: '📥', title: 'Import', desc: 'Import from file or URL', shortcut: `${mod}+I` },
    { id: 'export', icon: '📤', title: 'Export', desc: 'Export to various formats', shortcut: `${mod}+E` },
    { id: 'search', icon: '🔍', title: 'Search', desc: 'Search in specification', shortcut: `${mod}+F` },
    { id: 'preview', icon: '👁️', title: 'Preview', desc: 'Preview API documentation', shortcut: `${mod}+P` },
    { id: 'test-endpoint', icon: '🧪', title: 'Test Endpoint', desc: 'Test API endpoints live', shortcut: `${mod}+T` },
    { id: 'generate-sdk', icon: '📦', title: 'Generate SDK', desc: 'Generate client SDKs in multiple languages' },
    { id: 'schema-builder', icon: '🛠️', title: 'Schema Builder', desc: 'Build schemas visually' },
    { id: 'json-to-schema', icon: '🔄', title: 'JSON to Schema', desc: 'Convert JSON to schema' },
    { id: 'mock-server', icon: '🚀', title: 'Mock Server', desc: 'View mock server info' },
    { id: 'format', icon: '✨', title: 'Format Document', desc: 'Auto-format the spec', shortcut: `${mod}+Shift+F` },
    { id: 'toggle-dark', icon: '🌙', title: 'Toggle Dark Mode', desc: 'Switch between light and dark theme', shortcut: `${mod}+D` },
    { id: 'toggle-format', icon: '🔀', title: 'Toggle YAML/JSON', desc: 'Switch between YAML and JSON', shortcut: `${mod}+J` },
    { id: 'toggle-split', icon: '⚡', title: 'Toggle Split View', desc: 'Show editor and preview side-by-side', shortcut: `${mod}+\\` },
    { id: 'copy', icon: '📋', title: 'Copy to Clipboard', desc: 'Copy spec to clipboard', shortcut: `${mod}+C` },
    { id: 'share', icon: '🔗', title: 'Share URL', desc: 'Generate shareable link', shortcut: `${mod}+S` },
  ];

  const filtered = commands.filter(cmd =>
    cmd.title.toLowerCase().includes(query.toLowerCase()) ||
    cmd.desc.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    setSelected(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelected(s => (s + 1) % filtered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelected(s => (s - 1 + filtered.length) % filtered.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filtered[selected]) {
          onCommand(filtered[selected].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filtered, selected, onCommand]);

  return (
    <Overlay onClick={onClose}>
      <Palette onClick={(e) => e.stopPropagation()}>
        <SearchInput
          type="text"
          placeholder="Type a command or search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <CommandList>
          {filtered.map((cmd, index) => (
            <CommandItem
              key={cmd.id}
              $selected={index === selected}
              onClick={() => onCommand(cmd.id)}
            >
              <CommandIcon>{cmd.icon}</CommandIcon>
              <CommandContent>
                <CommandTitle>{cmd.title}</CommandTitle>
                <CommandDesc>{cmd.desc}</CommandDesc>
              </CommandContent>
              {cmd.shortcut && <CommandShortcut>{cmd.shortcut}</CommandShortcut>}
            </CommandItem>
          ))}
        </CommandList>
      </Palette>
    </Overlay>
  );
}

export default CommandPalette;
